import { dev } from '$app/environment';
import { IRK_API_URL, PUSHER_CHANNEL_KEY } from '$lib/constants';
import { captureException } from '@sentry/sveltekit';
import { session } from '$lib/stores';
import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import { writable, type Writable } from 'svelte/store';

class PusherConnection {
  private channel!: Pusher | undefined;
  private eventsRegistered: Record<string, Set<(value: any) => void>> = {};
  private privateChannel!: Channel;
  private privateEventsRegistered: Record<string, Set<(value: any) => void>> = {};
  private afterPrivateChannelFns: Array<() => void> = [];
  private isConnected = false;
  private isConnecting = false;
  private awaitableConnection!: ReturnType<typeof this.initPusherChannels>;

  isConnectedToPusher() {
    return this.isConnected;
  }

  async connect() {
    try {
      if (this.isConnected || this.isConnecting) {
        return;
      }
      this.eventsRegistered = {};
      this.privateEventsRegistered = {};

      this.awaitableConnection = this.initPusherChannels();
      this.channel = await this.awaitableConnection;
      this.handleSubscriptions();

      this.isConnected = !!this.channel;
    } catch (error) {
      captureException(error);
    }
  }

  async disconnect() {
    this.channel?.disconnect();
  }

  async subscribeToChannel(channelName: string) {
    if (!this.channel) {
      await this.awaitableConnection;
      return this.subscribeToChannel(channelName);
    }
    return Promise.resolve(this.channel.subscribe(channelName));
  }

  unsubscribeToChannel(channelName: string) {
    this.channel?.unsubscribe(channelName);
  }

  registerPublic(channel: Channel, eventName: string, callback: (value: any) => void) {
    if (!channel) {
      return false;
    }
    const keyName = `${channel.name}-${eventName}`;
    if (keyName in this.eventsRegistered) {
      this.eventsRegistered[keyName].add(callback);
    } else {
      this.eventsRegistered[keyName] = new Set([callback]);
      channel.bind(eventName, (value: any) => {
        this.eventsRegistered[keyName].forEach(cb => cb(value));
      });
    }
    return true;
  }

  unregisterPublic(channel: Channel, eventName: string, callback: (value: any) => void) {
    if (!channel) {
      return;
    }
    const keyName = `${channel.name}-${eventName}`;
    this.eventsRegistered[keyName]?.delete(callback);
  }

  emitPublic(channelName: string, eventName: string, value: any) {
    const keyName = `${channelName}-${eventName}`;
    this.eventsRegistered[keyName]?.forEach(cb => cb(value));
  }

  registerPrivate(eventName: string, callback: (value: any) => void) {
    if (!this.privateChannel) {
      this.afterPrivateChannelFns.push(() => this.registerPrivate(eventName, callback));
      return;
    }

    if (eventName in this.privateEventsRegistered) {
      this.privateEventsRegistered[eventName].add(callback);
    } else {
      this.privateEventsRegistered[eventName] = new Set([callback]);
      this.privateChannel.bind(eventName, (value: any) => {
        this.privateEventsRegistered[eventName].forEach(cb => cb(value));
      });
    }
  }

  unregisterPrivate(eventName: string, callback: (value: any) => void) {
    this.privateEventsRegistered[eventName]?.delete(callback);
  }

  async handleEventPresenceSubscriptions(
    eventId: string,
    options: {
      onUserJoined: (userId: string) => void;
      onUserLeft: (userId: string) => void;
    }
  ): Promise<{
    presentUsers: Writable<string[]>;
  }> {
    if (!this.channel) {
      await this.awaitableConnection;
      return await this.handleEventPresenceSubscriptions(eventId, options);
    }

    const presentUsers: Writable<string[]> = writable([]);
    const presence = this.channel.subscribe(`presence-event-${eventId}`);
    presence.bind('pusher:subscription_succeeded', members => {
      const ids: string[] = [];
      members.each(member => {
        ids.push(member.id);
        options?.onUserJoined?.(member.id);
      });
      presentUsers.set(ids);
    });
    presence.bind('pusher:subscription_error', error => {
      console.error('Error subscribing to presence channel: ', error);
    });
    presence.bind('pusher:member_added', member => {
      presentUsers.update(ref => [...ref, member.id]);
      options?.onUserJoined?.(member.id);
    });
    presence.bind('pusher:member_removed', member => {
      presentUsers.update(ref => ref.filter(id => id !== member.id));
      options?.onUserLeft?.(member.id);
    });
    return { presentUsers };
  }

  private async handlePrivateSubscriptions() {
    const id = session.get().irkAccount?.id;
    if (id) {
      this.privateChannel = this.channel!.subscribe(`private-user-${id}`);
      // Run pending registrations
      this.afterPrivateChannelFns.forEach(fn => fn());
      this.afterPrivateChannelFns = [];
    }
  }

  private handleSubscriptions() {
    if (!this.channel) {
      return;
    }
    this.handlePrivateSubscriptions();
  }

  private async initPusherChannels() {
    try {
      this.isConnecting = true;
      const PusherJS = (await import('pusher-js')).default;
      if (PusherJS) {
        PusherJS.logToConsole = dev;
        const headersProvider = () => {
          const accessToken = session.get().accessToken;
          return { authorization: `Bearer ${accessToken}` };
        };
        // Create the client
        const client = new PusherJS(PUSHER_CHANNEL_KEY, {
          cluster: 'us3',
          // Authenticate the user with the backend
          userAuthentication: {
            endpoint: `${IRK_API_URL}/pusher/user`,
            transport: 'ajax',
            headersProvider
          },
          // Authorize the user to a private channel
          channelAuthorization: {
            endpoint: `${IRK_API_URL}/pusher/auth`,
            transport: 'ajax',
            headersProvider
          }
        });
        client.signin();
        return client;
      }
    } catch (error) {
      captureException(error);
    } finally {
      this.isConnecting = false;
    }
  }
}

export const PusherClient = new PusherConnection();
